import { sanitizeEntries, type LibraryEntry } from "../src/domain/library-entry";
import type { Creation } from "../src/domain/types";

export type Entry = LibraryEntry;

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const KEY_PREFIX = "mymods.library.v1";

export class Library {
  private readonly key: string;

  constructor(private readonly storage: KeyValueStorage, username: string) {
    this.key = `${KEY_PREFIX}.${username.toLowerCase()}`;
  }

  all(): Entry[] {
    return sanitizeEntries(this.read());
  }

  replaceAll(entries: Entry[]): void {
    this.write(entries);
  }

  save(creation: Creation, icon: string | null): void {
    this.write([...this.all(), { creation, icon }]);
  }

  replace(index: number, creation: Creation, icon: string | null): void {
    const entries = this.all();
    entries[index] = { creation, icon };
    this.write(entries);
  }

  remove(index: number): void {
    this.write(this.all().filter((_, i) => i !== index));
  }

  private read(): unknown[] {
    try {
      const parsed = JSON.parse(this.storage.getItem(this.key) ?? "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private write(entries: Entry[]): void {
    this.storage.setItem(this.key, JSON.stringify(entries));
  }
}

export function mergeLibraries(local: Entry[], remote: Entry[]): Entry[] {
  const localIds = new Set(local.map((e) => e.creation.id));
  return [...local, ...remote.filter((e) => !localIds.has(e.creation.id))];
}
